import { Grid, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { insertIf, propertyTypesForBorrower } from '@roc/client-portal-shared/utils';
import {
  PropertyAddress,
  PropertyOwnership,
  OutstandingLoanOnTheProperty,
  PurchaseDate,
  PayoffAmount,
  CashOut,
  PurchasePrice,
  WhatWasThePurchasePrice,
  PropertyType,
  CurrentMarketValue,
  MonthlyMarketRent,
  AnnualTaxes,
  AnnualInsurance,
  AnnualHOAProvidedAtOriginationValue,
  CurrentlyOccupied,
  RenovationBugetExpent,
  PropertyCounty,
  Section8,
} from './components/propertyInformationFields';
import {
  PropertyInformationTimeline,
  TimeLineAnnualInsurance,
  TimelineIconMarketValue,
  TimelineIconPurchasePrice,
  TimelineAnnualTaxes,
  TimeLineMonthlyRent,
  TimeLineAnnualHoa,
  TimelineIconRenoSpent
} from './components/propertyInformationTimeLine';
import { ItemDisplay } from './components/itemDisplay';
import { formatCurrency } from '@roc/ui/utils';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    position: 'relative',
    maxWidth: '1080px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  activeConnector: {
    height: '50px',
    backgroundColor: theme.palette.primary.main,
    width: '4px',
  },
  inactiveConnector: {
    height: '50px',
    width: '4px',
  },
  timelineDot: {},
  symbols: {
    color: '#BABEC5',
    fontSize: '60px',
    fontWeight: 300
  },
  substractionContainer: {
    display: 'flex',
    width: '90%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '20px'
  },
  timeLineContainer: {
    maxWidth: '1080px',
    marginLeft: 'auto',
    marginRight: 'auto',
  }
}));
const MONTHS_IN_A_YEAR = 12;
export const SinglePropertyInformationForm = observer(
  ({
    store,
    substep,
    quoteStore
  }: any) => {
    const classes = useStyles();

    const isPropertyOwnedByBorrower = store?.form?.fields?.propertyOwnership?.value;
    const calculatedIncome = (Number(store?.form?.fields?.monthlyMarketRent?.value) ?? 0) * MONTHS_IN_A_YEAR;
    const calculatedExpenses = (Number(store?.form?.fields?.annualHOA?.value) ?? 0) + (Number(store?.form?.fields?.annualTaxes?.value) ?? 0) + (Number(store?.form?.fields?.annualInsurance?.value) ?? 0);
    const calculatedOperatingIncome = calculatedIncome - calculatedExpenses;
    function saveHandler() {
      const isValid = store.validateAndCleanupProperty(
        'Please complete all the required fields'
      );
      if (isValid) {
        store.setEditMode(false);
      }
    }
    return (
      <>
        {substep === 1 && <Grid
          container
          spacing={2}
          className={classes.container}
        >
          <Grid item xs={12}>
            <PropertyAddress singlePropertyQuoteStore={quoteStore} store={store} />
          </Grid>
          <Grid item xs={8}>
            <PropertyType store={store} options={propertyTypesForBorrower} />
          </Grid>
          <Grid item xs={4}>
            <PropertyCounty store={store} singlePropertyQuoteStore={quoteStore} />
          </Grid>
          <Grid item xs={6}>
            <PropertyOwnership store={store} />
          </Grid>
          {
            !isPropertyOwnedByBorrower && <Grid item xs={6}>
              <Section8 store={store} />
            </Grid>
          }
          {
            !isPropertyOwnedByBorrower && <Grid item xs={12}>
              <CurrentlyOccupied store={store} />
            </Grid>
          }
          {isPropertyOwnedByBorrower && <Grid item xs={6}>
            <PurchaseDate store={store} />
          </Grid>}
          {
            isPropertyOwnedByBorrower && <Grid item xs={6}>
              <OutstandingLoanOnTheProperty store={store} />
            </Grid>
          }
          {
            isPropertyOwnedByBorrower && <Grid item xs={6}>
              <PayoffAmount store={store} />
            </Grid>
          }
          {
            isPropertyOwnedByBorrower && <Grid item xs={6}>
              <CashOut store={store} />
            </Grid>
          }
          {isPropertyOwnedByBorrower && <Grid item xs={6}>
            <Section8 store={store} />
          </Grid>}
        </Grid>}
        {
          substep === 2 && <Grid item xs={12} className={classes.timeLineContainer}>
            <PropertyInformationTimeline
              items={[
                ...insertIf(!isPropertyOwnedByBorrower, [{
                  icon: <TimelineIconPurchasePrice />,
                  right: <PurchasePrice store={store} />,
                }]),
                ...insertIf(isPropertyOwnedByBorrower, [{
                  icon: <TimelineIconPurchasePrice />,
                  left: <WhatWasThePurchasePrice store={store} />,
                }]),
                ...insertIf(isPropertyOwnedByBorrower, [{
                  icon: <TimelineIconRenoSpent />,
                  left: <RenovationBugetExpent store={store} />,
                }]),
                {
                  icon: <TimelineIconMarketValue />,
                  right: <CurrentMarketValue store={store} />,
                },
                {
                  icon: <TimeLineMonthlyRent />,
                  right: <MonthlyMarketRent store={store} />,
                  gray: true
                },
                {
                  icon: <TimelineAnnualTaxes />,
                  right: <AnnualTaxes store={store} />,
                  gray: true
                },
                {
                  icon: <TimeLineAnnualInsurance />,
                  right: <AnnualInsurance store={store} />,
                  gray: true
                },
                {
                  icon: <TimeLineAnnualHoa />,
                  right: <AnnualHOAProvidedAtOriginationValue store={store} />,
                  gray: true
                }
              ]}
            />
            <Box className={classes.substractionContainer}>
              <ItemDisplay label="Total Annual Income " value={`${formatCurrency(calculatedIncome ?? 0)}`}></ItemDisplay>
              <Typography className={classes.symbols} align="center" variant="h4">
                -
              </Typography>
              <ItemDisplay label="Annual Expenses" value={`${formatCurrency(calculatedExpenses ?? 0)}`}></ItemDisplay>
              <Typography className={classes.symbols} align="center" variant="h4">
                =
              </Typography>
              <ItemDisplay label="Annual Net Operating income" value={`${formatCurrency(calculatedOperatingIncome ?? 0)}`}></ItemDisplay>
            </Box>
          </Grid>
        }
      </>
    );
  }
);
